import { Navigate, Route, Routes } from 'react-router';
import { useSession } from './auth/session';
import { Layout } from './components/Layout';
import { AccountDetailPage, AccountsPage } from './pages/Accounts';
import { AdminPage } from './pages/Admin';
import { ApiPage } from './pages/Api';
import {
  BillingCancelledPage,
  BillingReturnPage,
  LoginPage,
  OnboardingPage,
  PlansPage,
  SignupPage,
  VerifyPage,
} from './pages/Auth';
import { AutomationsPage } from './pages/Automations';
import { ConnectCallbackPage } from './pages/ConnectCallback';
import { ConnectConsentPage } from './pages/ConnectConsent';
import { ContactProfilePage, ContactsPage } from './pages/Contacts';
import { DashboardPage } from './pages/Dashboard';
import { FlowEditorPage, FlowsPage } from './pages/flows/Flows';
import { InboxPage } from './pages/Inbox';
import { InsightsPage } from './pages/Insights';
import { LandingPage } from './pages/Landing';
import {
  ContactPage,
  DataDeletionPage,
  DeletionStatusPage,
  PrivacyPage,
  TermsPage,
} from './pages/Legal';
import { NotificationsPage } from './pages/Notifications';
import { ForgotPasswordPage, ResetPasswordPage } from './pages/Password';
import { PostsPage } from './pages/Posts';
import { ReportsPage } from './pages/Reports';
import { SettingsPage } from './pages/Settings';
import { StoriesPage } from './pages/Stories';
import { TeamPage } from './pages/Team';

function RequireSession({ children }: { children: React.ReactElement }) {
  const { me, loading, unavailable, refresh } = useSession();
  if (loading) return <div className="auth-shell">Loading…</div>;
  if (unavailable)
    return (
      <div className="auth-shell">
        <div className="alert alert-error" role="alert">
          The service is unreachable right now. Your session is kept; try again in a moment.
        </div>
        <button type="button" className="btn btn-primary" onClick={() => void refresh()}>
          Retry
        </button>
      </div>
    );
  if (!me) return <Navigate to="/login" replace />;
  if (!me.emailVerified) return <Navigate to="/verify" replace />;
  if (!me.onboarding.completed) return <Navigate to="/onboarding" replace />;
  if (!me.onboarding.subscribed) return <Navigate to="/plans" replace />;
  return children;
}

export function App() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/verify" element={<VerifyPage />} />
      <Route path="/forgot" element={<ForgotPasswordPage />} />
      <Route path="/reset" element={<ResetPasswordPage />} />
      <Route path="/onboarding" element={<OnboardingPage />} />
      <Route path="/plans" element={<PlansPage />} />
      <Route path="/billing/return" element={<BillingReturnPage />} />
      <Route path="/billing/cancelled" element={<BillingCancelledPage />} />
      {/* LEG-01..LEG-04, LEG-09: public legal pages Meta App Review links to */}
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/data-deletion" element={<DataDeletionPage />} />
      <Route path="/data-deletion/status" element={<DeletionStatusPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route
        path="/connect/instagram/consent"
        element={
          <RequireSession>
            <ConnectConsentPage />
          </RequireSession>
        }
      />
      <Route
        path="/connect/instagram/callback"
        element={
          <RequireSession>
            <ConnectCallbackPage />
          </RequireSession>
        }
      />
      <Route
        path="/app"
        element={
          <RequireSession>
            <Layout />
          </RequireSession>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="accounts" element={<AccountsPage />} />
        <Route path="accounts/:id" element={<AccountDetailPage />} />
        <Route path="automations" element={<AutomationsPage />} />
        <Route path="flows" element={<FlowsPage />} />
        <Route path="flows/:id" element={<FlowEditorPage />} />
        <Route path="inbox" element={<InboxPage />} />
        <Route path="posts" element={<PostsPage />} />
        <Route path="stories" element={<StoriesPage />} />
        <Route path="contacts" element={<ContactsPage />} />
        <Route path="contacts/:id" element={<ContactProfilePage />} />
        <Route path="insights" element={<InsightsPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="api" element={<ApiPage />} />
        <Route path="team" element={<TeamPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="admin" element={<AdminPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
